'use client';

import toast from 'react-hot-toast';
import type { CurrentUserHrContext } from '@/app/hr/components/types';

interface MeReviewsTabProps { ctx: CurrentUserHrContext; }

const MeReviewsTab = ({ ctx }: MeReviewsTabProps) => {
  const visibleReviews = ctx.reviews
    .filter(r => r.status === 'released' || r.status === 'acknowledged')
    .sort((a, b) => new Date(b.review_period_end).getTime() - new Date(a.review_period_end).getTime());

  if (visibleReviews.length === 0) {
    return (
      <div className="p-8 rounded-lg border border-dashed border-gray-200 text-center">
        <p className="text-[13px] font-semibold text-gray-600">No released reviews yet</p>
        <p className="text-[11px] text-gray-400 mt-1">Once your manager releases a review, it will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {visibleReviews.map(rev => {
        const periodLabel = `${new Date(rev.review_period_start).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} – ${new Date(rev.review_period_end).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
        const isAcknowledged = rev.status === 'acknowledged' || !!rev.acknowledged_at;

        return (
          <div key={rev.id} className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <p className="text-[13px] font-bold text-[#1C2E5E]">
                  {rev.review_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} Review
                </p>
                <p className="text-[11px] text-gray-500">{periodLabel}</p>
                {rev.reviewer_name && <p className="text-[11px] text-gray-500 mt-0.5">By {rev.reviewer_name}</p>}
              </div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold ${isAcknowledged ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                {isAcknowledged ? 'ACKNOWLEDGED' : 'RELEASED'}
              </span>
            </div>

            {typeof rev.overall_rating === 'number' && (
              <div className="mb-3 pb-3 border-b border-gray-100">
                <p className="text-[10px] uppercase tracking-wide text-gray-500 font-bold mb-1">Overall</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-[#1C2E5E]">{rev.overall_rating.toFixed(1)}</span>
                  <span className="text-[11px] text-gray-500">/ 5.0</span>
                  <div className="flex items-center gap-0.5 ml-2">
                    {[1, 2, 3, 4, 5].map(i => (
                      <svg key={i} className="w-4 h-4" viewBox="0 0 24 24" fill={(rev.overall_rating ?? 0) >= i ? '#F5A623' : '#e5e7eb'} aria-hidden="true">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 mb-3 text-[11px]">
              {[
                { key: 'rating_communication', label: 'Communication' },
                { key: 'rating_quality_of_work', label: 'Quality of Work' },
                { key: 'rating_reliability', label: 'Reliability' },
                { key: 'rating_teamwork', label: 'Teamwork' },
                { key: 'rating_initiative', label: 'Initiative' },
                { key: 'rating_safety', label: 'Safety' },
              ].map(r => {
                const v = (rev as any)[r.key] as number | null;
                if (v === null || typeof v !== 'number') return null;
                return (
                  <div key={r.key} className="flex items-center justify-between rounded bg-gray-50 px-2 py-1">
                    <span className="text-gray-600">{r.label}</span>
                    <span className="font-bold text-[#1C2E5E]">{v.toFixed(1)}</span>
                  </div>
                );
              })}
            </div>

            {rev.strengths && (
              <div className="mb-2">
                <p className="text-[10px] uppercase tracking-wide text-green-700 font-bold mb-0.5">Strengths</p>
                <p className="text-[11px] text-gray-700 leading-relaxed">{rev.strengths}</p>
              </div>
            )}
            {rev.areas_for_improvement && (
              <div className="mb-2">
                <p className="text-[10px] uppercase tracking-wide text-amber-700 font-bold mb-0.5">Areas for Improvement</p>
                <p className="text-[11px] text-gray-700 leading-relaxed">{rev.areas_for_improvement}</p>
              </div>
            )}
            {rev.goals_next_period && (
              <div className="mb-2">
                <p className="text-[10px] uppercase tracking-wide text-blue-700 font-bold mb-0.5">Goals for Next Period</p>
                <p className="text-[11px] text-gray-700 leading-relaxed">{rev.goals_next_period}</p>
              </div>
            )}

            {!isAcknowledged && (
              <button
                type="button"
                onClick={() => toast('Review acknowledgment coming in SS-4', { icon: '✍️' })}
                className="mt-3 w-full px-3 py-2 rounded-lg text-[12px] font-bold text-white bg-[#E89A3C] hover:bg-[#0891c9] transition-colors"
              >
                Acknowledge Review
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MeReviewsTab;
